import { cn } from '@/shared/lib/utils'

type CashbackBadgeProps = {
  /** `null`/`undefined` renders nothing — callers don't need their own null-check. */
  amount: number | null | undefined
  /** Any valid CSS `background` value — each section supplies its own gold/orange tone. */
  background: string
  /** Color of the "₹" and amount text — most sections use white on their gold/orange background. */
  amountTextColor?: string
  /** Reveals the "cashback" label; collapsed shows only the ₹ amount. */
  expanded?: boolean
  /**
   * Uniformly shrinks every measurement below the Figma-spec size (1 = the
   * exact Figma size used elsewhere). A real size reduction — not a CSS
   * `transform: scale`, which only shrinks paint and leaves the box's actual
   * layout width unchanged — so a smaller badge genuinely takes less room in
   * a flex row instead of still risking overflow/clipping at its real size.
   */
  scale?: number
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
}

/** How much the width-consuming measurements shrink (relative to `scale`) once expanded. */
const EXPAND_WIDTH_SHRINK = 0.8

/** Figma-spec measurements (px) at scale = 1. */
const FIGMA_BASE_PX = {
  height: 20.57,
  radius: 7.26,
  paddingLeft: 7.26,
  paddingRight: 13,
  amountSymbolFont: 12.1,
  amountDigitsFont: 14.52,
  lineHeight: 18.15,
  labelFont: 7.26,
  labelMaxWidth: 40,
  labelMarginLeft: 4,
  labelTranslateY: 2,
  dotSize: 4.84,
  dotInset: 3.63,
  glareSize: 6,
  glareLeft: 0.63,
  glareTop: 13.94,
}

const CashbackBadge = ({
  amount,
  background,
  amountTextColor = '#FFFFFF',
  expanded = false,
  scale = 1,
  onClick,
  className,
}: CashbackBadgeProps) => {
  // Falsy, not just nullish: a literal ₹0 is meaningless to show as "₹0
  // cashback" — the doc's ₹5 floor means a real cashback is never 0, so a 0
  // here can only be a data glitch, not something worth rendering.
  if (!amount) return null

  // Expanding always tightens the *width*-consuming measurements (text,
  // padding, label) by the same factor, regardless of digit count — a
  // per-length threshold made short and long amounts grow by different
  // amounts, so only some of them visibly crept out of the card to the left
  // (the button is right-anchored via `ml-auto`, so any extra width it gains
  // extends its left edge, not its right). The badge's own height/radius/dot
  // stay at `scale`, so expanding never visibly resizes the box itself.
  const widthScale = expanded ? scale * EXPAND_WIDTH_SHRINK : scale
  const toPx = (value: number) => `${value * scale}px`
  const toPxWidth = (value: number) => `${value * widthScale}px`

  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClick?.(event)
      }}
      aria-label={`₹${amount} cashback`}
      aria-expanded={expanded}
      className={cn(
        'relative flex items-center overflow-clip shadow-[0px_0px_2px_1px_rgba(0,0,0,0.25)]',
        className,
      )}
      style={{
        background,
        height: toPx(FIGMA_BASE_PX.height),
        borderRadius: toPx(FIGMA_BASE_PX.radius),
        paddingLeft: toPxWidth(FIGMA_BASE_PX.paddingLeft),
        paddingRight: toPxWidth(FIGMA_BASE_PX.paddingRight),
      }}
    >
      {/* "₹22" and "cashback" are separate flex items (not one shared text run) so
          this box's own overflow-hidden never disturbs the other's baseline. */}
      <span
        className="whitespace-nowrap font-extrabold"
        style={{ color: amountTextColor, lineHeight: toPx(FIGMA_BASE_PX.lineHeight) }}
      >
        <span style={{ fontSize: toPxWidth(FIGMA_BASE_PX.amountSymbolFont) }}>₹</span>
        <span style={{ fontSize: toPxWidth(FIGMA_BASE_PX.amountDigitsFont) }}>{amount}</span>
      </span>
      <span
        className="self-center overflow-hidden whitespace-nowrap font-extrabold text-[#001F5F] transition-[max-width,margin-left] duration-300 ease-out"
        style={{
          lineHeight: toPx(FIGMA_BASE_PX.lineHeight),
          fontSize: toPxWidth(FIGMA_BASE_PX.labelFont),
          // Kept unscaled: shrinking this nudge along with everything else at
          // small `scale` values (e.g. the category grid) made the label look
          // vertically centered instead of sitting low like the Figma spec.
          transform: `translateY(${FIGMA_BASE_PX.labelTranslateY}px)`,
          marginLeft: expanded ? toPxWidth(FIGMA_BASE_PX.labelMarginLeft) : 0,
          maxWidth: expanded ? toPxWidth(FIGMA_BASE_PX.labelMaxWidth) : 0,
        }}
      >
        cashback
      </span>
      <span
        aria-hidden
        className="absolute rounded-full bg-white shadow-[0px_1.21px_2.42px_0px_rgba(0,0,0,0.5)]"
        style={{
          right: toPx(FIGMA_BASE_PX.dotInset),
          top: toPx(FIGMA_BASE_PX.dotInset),
          width: toPx(FIGMA_BASE_PX.dotSize),
          height: toPx(FIGMA_BASE_PX.dotSize),
        }}
      />
      {/* Bottom-left glare: Figma fills the badge with a radial gradient whose
          hotspot sits near the bottom-left corner. Its gradientTransform maps
          to a ~3.42x0.91px ellipse rotated 45°, centered 3.63px/16.94px from
          the top-left. A DOM element that thin rounds away at sub-pixel
          sizes, so the ellipse is painted as a gradient inside a normal-sized
          box instead — the box's own edges are invisible, only the gradient
          paints. */}
      <span
        aria-hidden
        className="absolute rotate-45"
        style={{
          left: toPx(FIGMA_BASE_PX.glareLeft),
          top: toPx(FIGMA_BASE_PX.glareTop),
          width: toPx(FIGMA_BASE_PX.glareSize),
          height: toPx(FIGMA_BASE_PX.glareSize),
          background:
            'radial-gradient(ellipse 57% 15% at 50% 50%, #FFFFFF 0%, #FFFFFF 65%, rgba(255,255,255,0) 100%)',
        }}
      />
    </button>
  )
}

export default CashbackBadge
